import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { LinkColumn } from "@/components/tableColumns/LinkColumn";
import { StructureNameCell } from "@/components/tableColumns/StructureNameCell";
import { formatAdaptive } from "@/utils/number";
import { NetworkTypeBadge } from "@/components//badges/NetworkTypeBadge";
import { getBreakdownSegments, hasBreakdownInvalidData, IAndIDistribution } from "@/components/IAndI/DistributionBar";
import { Details } from "@/components/IAndI/Details";
import { FC } from "react";
import { DateTimeColumn } from "@/components/tableColumns/DateTimeColumn";
import { observer } from "mobx-react-lite";
import { ColumnUnits, IAndIListColumnApis, ColumnMaxApi } from "./useIAndIListColumnMaxes";

const columnHelper = createColumnHelper<Dryp.IAndISummary>();

interface ProgressBarProps {
	totalValue: number;
	value?: number;
}
const ProgressBar: FC<ProgressBarProps> = ({ totalValue, value }) => {
	if (!totalValue || !value) {
		return null;
	}
	const ratio = (value / totalValue) * 100;
	return (
		<div className="relative w-full h-1.5 bg-muted rounded-full overflow-hidden mt-1">
			<div
				className="absolute top-0 left-0 h-full rounded-full bg-blue-400/70 transition-all duration-500"
				style={{ width: `${Math.min(ratio, 100)}%` }}
			/>
		</div>
	);
};

const MaxBarCell = observer(
	({
		api,
		getMax,
		value,
	}: {
		api: ColumnMaxApi;
		getMax: (topItem: Dryp.IAndISummary) => number | undefined;
		value?: number;
	}) => {
		if (api.loading) {
			return <div className="h-4 w-full animate-pulse rounded bg-muted" />;
		}
		const topItem = api.data?.items?.[0];
		const max = topItem ? getMax(topItem) : undefined;
		return (
			<div>
				<ProgressBar totalValue={max ?? 0} value={value} />
				<div className="text-right text-xs font-mono tabular-nums mt-0.5">
					{value == null ? "—" : formatAdaptive(value)}
				</div>
			</div>
		);
	},
);

export const getIAndIv1Columns = (
	columnApis: IAndIListColumnApis,
	units: ColumnUnits,
	onConfigured?: () => void,
): ColumnDef<Dryp.IAndISummary, any>[] => {
	return [
		{
			id: "structureId",
			accessorKey: "structure.id",
			header: "Structure id",
		},
		LinkColumn({
			id: "structureName",
			accessorKey: "structure.name",
			header: "Name",
			className: "font-medium tracking-tight",
			linkText: row => <StructureNameCell name={row.structure.name} isActive={row.structure.isActive} />,
			link: row => ({
				to: "Dashboard.Structures.Structure",
				params: { id: row.structure.id },
				query: {},
			}),
			tooltipText: row => `Go to ${row.structure.name}`,
		}),
		columnHelper.accessor("structure.id", {
			header: "I&I Distribution",
			id: "_IAndI_distribution",
			enableSorting: false,
			cell: ({ row }) => {
				const summary = row.original;
				const breakdown = summary.parts;
				if (!breakdown) {
					return <IAndIDistribution summary={summary} onConfigured={onConfigured} />;
				}
				const hasInvalidData = hasBreakdownInvalidData(getBreakdownSegments(breakdown));
				return (
					<div className="min-w-32 max-w-64 w-full">
						<Tooltip>
							<TooltipTrigger asChild>
								<div className="min-w-32 max-w-64 w-full">
									<IAndIDistribution summary={summary} showValues />
								</div>
							</TooltipTrigger>
							{!hasInvalidData && (
								<TooltipContent className="max-w-xs text-xs leading-snug text-white ">
									<Details breakdown={breakdown} period={summary.period} />
								</TooltipContent>
							)}
						</Tooltip>
					</div>
				);
			},
			meta: {
				exportFormat(_, row) {
					const breakdown = row.parts;
					if (!breakdown) {
						return "";
					}

					const inflowValue = formatAdaptive(breakdown.inflow.flow?.value || 0);
					const inflowRatio = (breakdown.inflow.ratio?.value || 0).toFixed(1);
					const inflowRatioUnit = breakdown.inflow.ratio?.unit.symbol || "";

					const wasteValue = formatAdaptive(breakdown.waste.flow?.value || 0);
					const wasteRatio = (breakdown.waste.ratio?.value || 0).toFixed(1);
					const wasteFlowUnit = breakdown.waste.flow?.unit.symbol || "";
					const wasteRatioUnit = breakdown.waste.ratio?.unit.symbol || "";

					const infiltrationValue = formatAdaptive(breakdown.infiltration.flow?.value || 0);
					const infiltrationRatio = (breakdown.infiltration.ratio?.value || 0).toFixed(1);
					const infiltrationFlowUnit = breakdown.infiltration.flow?.unit.symbol || "";
					const infiltrationRatioUnit = breakdown.infiltration.ratio?.unit.symbol || "";

					return `Inflow: ${inflowValue} ${breakdown.inflow.flow?.unit.symbol ?? ""} (${inflowRatio}${inflowRatioUnit}), Wastewater: ${wasteValue} ${wasteFlowUnit} (${wasteRatio}${wasteRatioUnit}), Infiltration: ${infiltrationValue} ${infiltrationFlowUnit} (${infiltrationRatio}${infiltrationRatioUnit})  `;
				},
			},
		}),
		columnHelper.accessor("parts.infiltration.volume.value", {
			id: "infiltrationVolume",
			header: "Infiltration Volume",
			cell: row => (
				<MaxBarCell
					api={columnApis.infiltrationVolume}
					getMax={topItem => topItem.parts?.infiltration.volume?.value}
					value={row.getValue()}
				/>
			),
			meta: {
				unit: units.volume,
				sortableHeaderClass: "justify-self-end",
				exportFormat(_, row) {
					const val = row.parts?.infiltration?.volume?.value;
					if (!val) {
						return "-";
					}
					return formatAdaptive(val);
				},
			},
		}),

		columnHelper.accessor("parts.inflow.volume.value", {
			id: "inflowVolume",
			cell: row => (
				<MaxBarCell
					api={columnApis.inflowVolume}
					getMax={topItem => topItem.parts?.inflow.volume?.value}
					value={row.getValue()}
				/>
			),
			header: "Inflow volume",
			meta: {
				unit: units.volume,
				sortableHeaderClass: "justify-self-end",
				headerClassName: "justify-self-end",
				exportFormat(_, row) {
					const val = row.parts?.inflow?.volume?.value;
					if (!val) {
						return "-";
					}
					return formatAdaptive(val);
				},
			},
		}),

		columnHelper.accessor("parts.infiltration.flowPerLength.value", {
			id: "iAndIBreakdown_infiltration_flowPerPipeLength",
			cell: row => (
				<MaxBarCell
					api={columnApis.infiltrationFlowPerLength}
					getMax={topItem => topItem.parts?.infiltration.flowPerLength?.value}
					value={row.getValue()}
				/>
			),
			header: "Infiltration per pipe length",
			meta: {
				unit: units.flowPerLength,
				sortableHeaderClass: "justify-self-end",
				exportFormat(_, row) {
					const val = row.parts?.infiltration?.flowPerLength?.value;
					if (!val) {
						return "-";
					}
					return formatAdaptive(val);
				},
			},
		}),

		columnHelper.accessor("parts.inflow.flowPerLength.value", {
			id: "iAndIBreakdown_inflow_flowPerPipeLength",
			cell: row => (
				<MaxBarCell
					api={columnApis.inflowFlowPerLength}
					getMax={topItem => topItem.parts?.inflow.flowPerLength?.value}
					value={row.getValue()}
				/>
			),
			header: "Inflow per pipe length",
			meta: {
				unit: units.flowPerLength,
				sortableHeaderClass: "justify-self-end",
				exportFormat(_, row) {
					const val = row.parts?.inflow?.flowPerLength?.value;
					if (!val) {
						return "-";
					}
					return formatAdaptive(val);
				},
			},
		}),

		{
			accessorKey: "structure.networkType",
			header: "Network Type",
			enableSorting: false,
			cell: ({ row }) => {
				const networkType = row.original.structure.networkType;
				return <NetworkTypeBadge type={networkType} showLabel />;
			},
		},
		DateTimeColumn({
			accessorKey: "period.createdAt",
			id: "createdAt",
			header: "Checked at",
			format: "dd LLL yyyy, HH:mm",
			align: "left",
			datetimeClassName: "text-sm",
		}),
	];
};
